import { ReactNode } from 'react';

import { Banner, Flex, ProgressCircle } from '@spotify-internal/encore-web';
import { isNotFoundError } from '@spotify/confidence-graphql';

import { NotFoundContent } from '../NotFoundContent';
import Body from './Body';

import styles from './ResourceGuard.module.css';

export type ResourceGuardProps = {
  error?: Error | null;
  loading?: boolean;
  loadingFallback?: ReactNode;
  children: ReactNode;
};

const FallbackShell = ({ children }: { children: ReactNode }) => (
  <Body>{children}</Body>
);

export const ResourceGuard = ({
  error,
  loading,
  loadingFallback,
  children,
}: ResourceGuardProps) => {
  if (error) {
    if (isNotFoundError(error)) {
      return (
        <FallbackShell>
          <NotFoundContent variant="resource" fillViewport={false} />
        </FallbackShell>
      );
    }

    return (
      <FallbackShell>
        <Banner colorSet="negative" contextual role="alert">
          {error.message}
        </Banner>
      </FallbackShell>
    );
  }

  if (loading) {
    return (
      <FallbackShell>
        {loadingFallback ?? (
          <Flex justifyContent="center" className={styles.loading}>
            <ProgressCircle
              size="large"
              semanticColor="textBrightAccent"
              aria-label="Loading"
            />
          </Flex>
        )}
      </FallbackShell>
    );
  }

  return <>{children}</>;
};

ResourceGuard.displayName = 'DialogComposite.ResourceGuard';

export default ResourceGuard;
